import { createContext } from "react";
import { REVIEWBOOST_V3_UI_ENABLED } from "./review-boost-v3-ui";
import { listedProducts, productState } from "./product-access";
import type { OrganizationContext } from "./types";

export interface ExperienceNavigationItem {
  path: string;
  label: string;
  mobileLabel: string;
  icon: string;
}

/** Presentation only. Product Access and product-native authorization remain authoritative. */
export interface ExperienceDefinition {
  key: string;
  displayName: string;
  mode: "standalone-product" | "vertical-solution";
  routeBase: string;
  homePath: string;
  /** Product-content style scope only; never grants shell/chrome ownership. */
  surfaceClassName?: string;
  /** Endorsed product presentation only. Never changes Platform account/session authority. */
  endorsedBrand?: { byline: string; logoPath: string };
  primaryNavigation: readonly ExperienceNavigationItem[];
}

export const reviewBoostV3Navigation: readonly ExperienceNavigationItem[] = Object.freeze([
  { path: "overview", label: "Inicio", mobileLabel: "Inicio", icon: "home" },
  { path: "reviews", label: "Reseñas", mobileLabel: "Reseñas", icon: "reputation" },
  { path: "acquisition", label: "Conseguir reseñas", mobileLabel: "Conseguir", icon: "customers" },
  { path: "settings", label: "Configuración", mobileLabel: "Ajustes", icon: "settings" },
]);

export const reviewBoostExperience: ExperienceDefinition = {
  key: "review-boost",
  displayName: "Review Boost",
  mode: "standalone-product",
  routeBase: "reputation",
  homePath: "overview",
  endorsedBrand: { byline: "by ClickDialog", logoPath: "/brand/review-boost-mark.svg" },
  primaryNavigation: REVIEWBOOST_V3_UI_ENABLED ? reviewBoostV3Navigation : [
    { path: "overview", label: "Inicio", mobileLabel: "Inicio", icon: "home" },
    { path: "reviews", label: "Reseñas", mobileLabel: "Reseñas", icon: "reputation" },
    { path: "acquisition", label: "Captación", mobileLabel: "Captación", icon: "customers" },
    { path: "ai-agent", label: "Agente IA", mobileLabel: "Agente", icon: "agent" },
    { path: "reports", label: "Reportes", mobileLabel: "Reportes", icon: "reports" },
    { path: "settings", label: "Configuración", mobileLabel: "Más", icon: "settings" },
  ],
};

export const salonsExperience: ExperienceDefinition = {
  key: "salons",
  displayName: "Peluquerías",
  mode: "vertical-solution",
  routeBase: "salons",
  homePath: "home",
  surfaceClassName: "cd-salons-shell",
  primaryNavigation: [
    { path: "home", label: "Inicio", mobileLabel: "Inicio", icon: "home" },
    { path: "conversations", label: "Conversaciones", mobileLabel: "Chats", icon: "conversations" },
    { path: "agenda", label: "Agenda", mobileLabel: "Agenda", icon: "calendar" },
    { path: "customers", label: "Clientes", mobileLabel: "Clientes", icon: "customers" },
    { path: "sales", label: "Ventas", mobileLabel: "Ventas", icon: "sales" },
    { path: "reports", label: "Reportes", mobileLabel: "Reportes", icon: "reports" },
    { path: "settings", label: "Administración", mobileLabel: "Admin", icon: "settings" },
  ],
};

export const commerceExperience: ExperienceDefinition = {
  key: "commerce",
  displayName: "Commerce Storefront",
  mode: "standalone-product",
  routeBase: "commerce",
  homePath: "stores",
  primaryNavigation: [
    { path: "stores", label: "Tiendas", mobileLabel: "Tiendas", icon: "home" },
    { path: "catalog", label: "Catálogo", mobileLabel: "Catálogo", icon: "sales" },
  ],
};

export const hospitalityExperience: ExperienceDefinition = {
  key: "hospitality",
  displayName: "Hospitality Agent",
  mode: "vertical-solution",
  routeBase: "hospitality",
  homePath: "home",
  surfaceClassName: "cd-hospitality-shell",
  primaryNavigation: [
    {path:"home",label:"Inicio",mobileLabel:"Inicio",icon:"home"},
    {path:"conversations",label:"Conversaciones",mobileLabel:"Chats",icon:"conversations"},
    {path:"knowledge",label:"Conocimiento",mobileLabel:"Datos",icon:"reports"},
    {path:"settings",label:"Configuración",mobileLabel:"Más",icon:"settings"},
  ],
};

/** Agent-first container rental product. Presentation only; registry and Platform control access. */
export const contenedoresExperience: ExperienceDefinition = {
  key: "contenedores",
  displayName: "ContenedorOS",
  mode: "vertical-solution",
  routeBase: "contenedores",
  homePath: "agent",
  surfaceClassName: "cd-contenedores-shell",
  primaryNavigation: [
    {path:"agent",label:"Mi agente",mobileLabel:"Agente",icon:"agent"},
    {path:"today",label:"Hoy",mobileLabel:"Hoy",icon:"home"},
    {path:"map",label:"Mapa inteligente",mobileLabel:"Mapa",icon:"reports"},
    {path:"assets",label:"Contenedores",mobileLabel:"Recursos",icon:"sales"},
    {path:"dispatch",label:"Despacho",mobileLabel:"Despacho",icon:"calendar"},
    {path:"orders",label:"Pedidos",mobileLabel:"Pedidos",icon:"sales"},
    {path:"quotes",label:"Presupuestos",mobileLabel:"Cotizar",icon:"sales"},
    {path:"customers",label:"Clientes",mobileLabel:"Clientes",icon:"customers"},
    {path:"leads",label:"Consultas",mobileLabel:"Consultas",icon:"conversations"},
    {path:"finance",label:"Finanzas",mobileLabel:"Finanzas",icon:"reports"},
    {path:"twin",label:"Twin Studio",mobileLabel:"Twin",icon:"reports"},
    {path:"knowledge",label:"Conocimiento",mobileLabel:"Datos",icon:"reports"},
    {path:"automation",label:"Equipo digital",mobileLabel:"Agentes",icon:"agent"},
    {path:"settings",label:"Configuración",mobileLabel:"Más",icon:"settings"}
  ],
};

const definitions: readonly ExperienceDefinition[] = Object.freeze([
  reviewBoostExperience,
  salonsExperience,
  commerceExperience,
  hospitalityExperience,
  contenedoresExperience,
]);

export const ExperienceContext = createContext<ExperienceDefinition | null>(null);

export function experienceDefinitionForRouteBase(routeBase: string): ExperienceDefinition | undefined {
  return definitions.find((definition) => definition.routeBase === routeBase);
}

export function experienceEntryPath(
  organization: OrganizationContext,
  experience: ExperienceDefinition,
): string {
  return `/o/${organization.organization.slug}/${experience.routeBase}/${experience.homePath}`;
}

export function experienceFor(
  organization: OrganizationContext | undefined,
  pathname: string,
): ExperienceDefinition | null {
  if (
    !organization
    || organization.organization.status !== "active"
    || organization.membership.status !== "active"
  ) return null;

  return definitions.find((definition) => {
    const base = `/o/${organization.organization.slug}/${definition.routeBase}`;
    const product = listedProducts(organization.products).find(
      ({ manifest }) => manifest.routeBase === definition.routeBase,
    );
    return (pathname === base || pathname.startsWith(`${base}/`))
      && productState(product?.manifest, product, true) === "available";
  }) ?? null;
}

export function organizationExperienceHome(organization: OrganizationContext): string {
  return `/o/${organization.organization.slug}/home`;
}